import type { MeResponse, PublicConfig } from '@licita/contracts';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { IconKey } from './components/icons';
import { Notice, ThemeToggle, Wordmark } from './components/ui';
import { api, ApiError } from './lib/api';
import { authClient } from './lib/auth';
import { queryClient } from './lib/queries';
import { router, SessionContext } from './router';

type State =
  | { kind: 'carregando' }
  | { kind: 'sem-sessao' }
  | { kind: 'cliente' }
  | { kind: 'cadastrar-passkey'; me: MeResponse }
  | { kind: 'entrar-passkey'; me: MeResponse }
  | { kind: 'ok'; me: MeResponse };

function Frame({ children, user, onSignOut }: { children: ReactNode; user?: string; onSignOut?: () => void }) {
  return (
    <div className="flex min-h-dvh flex-col">
      <header className="flex h-12 items-center justify-between gap-3 px-4 sm:px-6">
        <div className="flex items-center gap-2">
          <Wordmark />
          <span className="app-chip app-chip-accent">backoffice</span>
        </div>
        <div className="flex items-center gap-1.5">
          {user && <span className="hidden text-[12px] text-mute sm:inline">{user}</span>}
          {onSignOut && (
            <button type="button" className="app-btn app-btn-ghost" onClick={onSignOut}>
              Sair
            </button>
          )}
          <ThemeToggle />
        </div>
      </header>
      <main id="conteudo" className="flex flex-1 justify-center px-4 py-10">
        {children}
      </main>
    </div>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="app-card w-full max-w-[400px] self-start p-6">
      <h1 className="text-[18px] font-semibold tracking-[-0.2px]">{title}</h1>
      {children}
    </div>
  );
}

/** Backoffice da equipe. Acesso: passkey obrigatória; link mágico só para o primeiro acesso. */
export default function App() {
  const [state, setState] = useState<State>({ kind: 'carregando' });
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [staging, setStaging] = useState(false);
  useEffect(() => {
    void api<PublicConfig>('/config').then(
      (c) => setStaging(c.env !== 'production'),
      () => {},
    );
  }, []);

  const load = useCallback(async () => {
    try {
      const me = await api<MeResponse>('/me');
      if (me.user.role === 'cliente') return setState({ kind: 'cliente' });
      if (me.passkeys === 0) return setState({ kind: 'cadastrar-passkey', me });
      if (!me.session.passkeyVerified) return setState({ kind: 'entrar-passkey', me });
      setState({ kind: 'ok', me });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return setState({ kind: 'sem-sessao' });
      setMsg('Não foi possível falar com a API.');
      setState({ kind: 'sem-sessao' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const passkeyLogin = async () => {
    setBusy(true);
    setMsg(null);
    const r = await authClient.signIn.passkey();
    setBusy(false);
    if (r?.error) return setMsg('Não foi possível entrar com a passkey.');
    await load();
  };

  const registerPasskey = async () => {
    setBusy(true);
    setMsg(null);
    const r = await authClient.passkey.addPasskey({ name: 'Backoffice' });
    setBusy(false);
    if (r?.error) return setMsg('Não foi possível cadastrar a passkey.');
    setMsg('Passkey cadastrada. Agora entre com ela.');
    await load();
  };

  const magicLink = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    await authClient.signIn.magicLink({ email: email.trim().toLowerCase(), callbackURL: location.origin });
    setBusy(false);
    setMsg('Se houver uma conta da equipe com esse e-mail, o link chega em instantes.');
  };

  const signOut = async () => {
    await authClient.signOut().catch(() => {});
    setState({ kind: 'sem-sessao' });
  };

  const notice = msg && (
    <div className="mt-4">
      <Notice>{msg}</Notice>
    </div>
  );

  if (state.kind === 'carregando')
    return (
      <Frame>
        <div
          className="h-36 w-full max-w-[400px] animate-pulse self-start rounded-lg bg-bg-3 motion-reduce:animate-none"
          aria-busy="true"
        />
      </Frame>
    );

  if (state.kind === 'sem-sessao')
    return (
      <Frame>
        <Card title="Entrar no backoffice">
          <p className="mt-1.5 leading-relaxed text-fg-2">O acesso da equipe é por passkey.</p>
          <button type="button" className="app-btn app-btn-primary mt-5 w-full" onClick={passkeyLogin} disabled={busy}>
            <IconKey size={15} /> Entrar com passkey
          </button>
          <details className="mt-4 text-[13px]">
            <summary className="cursor-pointer py-1.5 text-accent">Primeiro acesso ou perdeu a passkey?</summary>
            <form onSubmit={magicLink} className="mt-2 flex flex-col gap-2">
              <label className="app-lbl" htmlFor="email">
                E-mail da equipe
              </label>
              <input
                id="email"
                type="email"
                required
                className="app-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              <button type="submit" className="app-btn app-btn-ghost w-full" disabled={busy || !email}>
                Receber link de acesso
              </button>
              <p className="text-[12px] text-mute">O link só serve para cadastrar uma passkey nova.</p>
            </form>
          </details>
          {notice}
        </Card>
      </Frame>
    );

  if (state.kind === 'cliente')
    return (
      <Frame onSignOut={signOut}>
        <Card title="Acesso restrito">
          <p className="mt-1.5 leading-relaxed text-fg-2">Esta área é da equipe do licita.dev. Seu painel fica em app.licita.dev.</p>
        </Card>
      </Frame>
    );

  if (state.kind === 'cadastrar-passkey')
    return (
      <Frame user={state.me.user.email} onSignOut={signOut}>
        <Card title="Cadastrar passkey">
          <p className="mt-1.5 leading-relaxed text-fg-2">
            No backoffice a passkey é obrigatória. Use a digital, o rosto ou a chave de segurança deste aparelho.
          </p>
          <button type="button" className="app-btn app-btn-primary mt-5 w-full" onClick={registerPasskey} disabled={busy}>
            <IconKey size={15} /> Cadastrar passkey
          </button>
          {notice}
        </Card>
      </Frame>
    );

  if (state.kind === 'entrar-passkey')
    return (
      <Frame user={state.me.user.email} onSignOut={signOut}>
        <Card title="Entrar com passkey">
          <p className="mt-1.5 leading-relaxed text-fg-2">Você entrou por link. Para usar o backoffice, confirme com a sua passkey.</p>
          <button type="button" className="app-btn app-btn-primary mt-5 w-full" onClick={passkeyLogin} disabled={busy}>
            <IconKey size={15} /> Entrar com passkey
          </button>
          {notice}
        </Card>
      </Frame>
    );

  return (
    <SessionContext.Provider value={{ me: state.me, staging, signOut: () => void signOut() }}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </SessionContext.Provider>
  );
}
